"use client";

import { useState, useEffect, useTransition } from "react";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import type { RegistrationData } from "@/types/auth";
import { useActionState } from "react";
import { useTranslations } from "next-intl";
import {
  registerAction,
  RegistrationDataInput,
  RegistrationResponse,
} from "@/app/actions/registration";

interface InitialFormProps {
  data: RegistrationData;
  onUpdateAction: (d: Partial<RegistrationData>) => void;
  onBackAction: () => void;
  onNextAction: () => void;
}

export default function InitialForm({
  data,
  onUpdateAction,
  onBackAction,
  onNextAction,
}: InitialFormProps) {
  const t = useTranslations("auth");
  const tCommon = useTranslations("common");
  const [firstName, setFirstName] = useState(data.firstName || "");
  const [lastName,  setLastName]  = useState(data.lastName  || "");
  const [username,  setUsername]  = useState(data.username  || "");
  const [password,  setPassword]  = useState(data.password  || "");
  const [email,     setEmail]     = useState(data.email     || "");
  const [showPassword, setShowPassword] = useState(false);
  const [errors,    setErrors]    = useState<Record<string,string>>({});

  // availability + checked flags
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [usernameChecked,  setUsernameChecked]  = useState(true);
  const [usernameError,    setUsernameError]    = useState<string|null>(null);

  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailChecked,  setEmailChecked]  = useState(true);
  const [emailError,    setEmailError]    = useState<string|null>(null);

  // password criteria
  const isDirty       = password.length > 0;
  const isLengthValid = password.length >= 12;
  const hasUppercase  = /[A-Z]/.test(password);
  const hasSpecial = /[!@#$%^&*()\[\],.?":{}|<>\/\\\-_]/.test(password);
  const passwordValid = isLengthValid && hasUppercase && hasSpecial;

  // debounce helper
  const debounce = (fn: () => void, ms = 500) => {
    let id: ReturnType<typeof setTimeout>;
    return () => {
      clearTimeout(id);
      id = setTimeout(fn, ms);
    };
  };

  // 1) username email validation (immediate)
  useEffect(() => {
    if (!username) {
      setUsernameError(null);
      return;
    }

    if (username.includes('@')) {
      setUsernameError(t("usernameIsEmail"));
    } else {
      // Only clear email error if it was an email error
      if (usernameError === "Username cannot be an email address") {
        setUsernameError(null);
      }
    }
  }, [username, usernameError]);

  // 2) username availability (debounced, only if not email)
  useEffect(() => {
    if (!username || username.includes('@')) {
      setUsernameChecked(true);
      setCheckingUsername(false);
      return;
    }

    setCheckingUsername(true);
    setUsernameChecked(false);

    debounce(async () => {
      // Double-check - if email was added during debounce, abort
      if (username.includes('@')) {
        setCheckingUsername(false);
        setUsernameChecked(true);
        return;
      }

      try {
        const res = await fetch(
          `/api/check-availability?username=${encodeURIComponent(username)}`
        );
        const j = await res.json();
        if (!username.includes("@")) {
          if (j.invalid && j.error) {
            setUsernameError(j.error);
          } else if (j.taken) {
            setUsernameError(t("usernameTaken"));
          } else {
            setUsernameError(null);
          }
        }
      } catch {
        if (!username.includes("@")) {
          setUsernameError(t("errorCheckingUsername"));
        }
      } finally {
        setCheckingUsername(false);
        setUsernameChecked(true);
      }
    })();
  }, [username]);

  // 3) email availability
  useEffect(() => {
    if (!email) {
      setEmailError(null);
      setEmailChecked(true);
      return;
    }
    setCheckingEmail(true);
    setEmailChecked(false);

    debounce(async () => {
      try {
        const res = await fetch(
          `/api/check-availability?email=${encodeURIComponent(email)}`
        );
        const j = await res.json();
        setEmailError(j.taken ? t("emailTaken") : null);
      } catch {
        setEmailError(t("errorCheckingEmail"));
      } finally {
        setCheckingEmail(false);
        setEmailChecked(true);
      }
    })();
  }, [email]);

  // 4) Server Action for registration
  const [isPending, startTransition] = useTransition();
  const [regState, runRegister] =
    useActionState<RegistrationResponse, FormData>(
      async (_, formData) => {
        const input: RegistrationDataInput = {
          firstName: formData.get("firstName")!.toString(),
          lastName:  formData.get("lastName")!.toString(),
          username:  formData.get("username")!.toString(),
          password:  formData.get("password")!.toString(),
          email:     formData.get("email")!.toString(),
        };
        return registerAction(input);
      },
      {}
    );

  useEffect(() => {
    if (regState.errors) {
      setErrors(regState.errors);
    } else if (regState.success) {
      onNextAction();
    }
  }, [regState, onNextAction]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});

    if (!firstName || !lastName || !username || !password || !email) {
      setErrors({ general: t("allFieldsRequired") });
      return;
    }
    if (!passwordValid) {
      setErrors({ password: t("passwordCriteria") });
      return;
    }
    if (usernameError || emailError) return;

    const formData = new FormData(e.currentTarget);
    onUpdateAction({ firstName, lastName, username, password, email });
    startTransition(() => runRegister(formData));
  };

  // disable button until all checks done without errors
  const hasInlineErrors = !!usernameError || !!emailError || Object.keys(errors).length > 0;

  const disableButton =
    !firstName ||
    !lastName ||
    !username ||
    !password ||
    !email ||
    !passwordValid ||
    !usernameChecked ||
    !emailChecked ||
    hasInlineErrors ||
    isPending;

  const inputClass =
    "w-full min-h-[48px] px-4 py-3.5 rounded-full border border-border bg-background text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors text-base";
  const grey = "text-muted-foreground";
  const red = "text-destructive";

  return (
    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5 md:space-y-6 pt-2 relative">
      <button
        onClick={onBackAction}
        type="button"
        className="absolute -top-1 -left-1 min-w-[44px] min-h-[44px] flex items-center justify-center p-2.5 hover:bg-muted/80 rounded-full text-muted-foreground hover:text-foreground transition-colors touch-manipulation"
        aria-label="Go back"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>

      <h1 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-foreground text-center mb-5 sm:mb-6 md:mb-8 leading-tight">
        {t("createAccount")}
      </h1>
      {errors.general && (
        <div className="p-3.5 rounded-xl bg-destructive/10 text-destructive text-sm border border-destructive/20 text-center">
          {errors.general}
        </div>
      )}

      <div>
        <input
          name="firstName"
          placeholder={t("firstName")}
          value={firstName}
          onChange={(e) => {
            setFirstName(e.target.value);
            onUpdateAction({ firstName: e.target.value });
          }}
          className={inputClass}
        />
        {errors.firstName && <p className="text-destructive text-xs mt-1.5">{errors.firstName}</p>}
      </div>

      <div>
        <input
          name="lastName"
          placeholder={t("lastName")}
          value={lastName}
          onChange={(e) => {
            setLastName(e.target.value);
            onUpdateAction({ lastName: e.target.value });
          }}
          className={inputClass}
        />
        {errors.lastName && <p className="text-destructive text-xs mt-1.5">{errors.lastName}</p>}
      </div>

      <div>
        <input
          name="username"
          placeholder={t("username")}
          value={username}
          maxLength={30}
          pattern="\S{1,30}"
          title="No spaces, max 30 characters"
          onChange={(e) => {
            const clean = e.target.value.replace(/\s+/g, "").slice(0, 30);
            setUsername(clean);
            onUpdateAction({ username: clean });
          }}
          className={inputClass}
        />
        {usernameError && <p className="text-destructive text-xs mt-1.5">{usernameError}</p>}
      </div>

      <div>
        <div className="relative">
          <input
            name="password"
            type={showPassword ? "text" : "password"}
            placeholder={t("password")}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              onUpdateAction({ password: e.target.value });
            }}
            className={`${inputClass} pr-12`}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>
        <ul className="mt-1.5 ml-4 list-disc list-inside text-sm">
          <li className={isDirty && !isLengthValid ? red : grey}>{t("atLeast12Chars")}</li>
          <li className={isDirty && !hasUppercase ? red : grey}>{t("oneUppercase")}</li>
          <li className={isDirty && !hasSpecial ? red : grey}>{t("oneSpecial")}</li>
        </ul>
        {errors.password && <p className="mt-1.5 text-xs text-destructive">{errors.password}</p>}
      </div>

      <div>
        <input
          name="email"
          type="email"
          placeholder={t("email")}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            onUpdateAction({ email: e.target.value });
            setEmailChecked(false);
          }}
          className={inputClass}
        />
        {emailError && <p className="text-destructive text-xs mt-1.5">{emailError}</p>}
      </div>

      <Button
        type="submit"
        className="w-full min-h-[48px] py-6 text-base rounded-full touch-manipulation"
        size="lg"
        disabled={disableButton}
      >
        {tCommon("continue")}
      </Button>

      <p className="text-center text-sm text-muted-foreground pt-1">
        {t("alreadyHaveAccount")}{" "}
        <Link href="/login" className="text-primary hover:underline">{t("loginLink")}</Link>
      </p>
      <p className="text-center text-xs text-muted-foreground">
        {tCommon.rich("agreeTerms", {
          privacy: (chunks) => <Link href="/privacy" className="underline hover:text-foreground">{chunks}</Link>,
          terms: (chunks) => <Link href="/terms" className="underline hover:text-foreground">{chunks}</Link>,
        })}
      </p>
    </form>
  );
}
